import { useState, useEffect, useCallback } from 'react'
import {
  X,
  Download,
  ExternalLink,
  FileText,
  Image,
  File,
  Loader2,
  ZoomIn,
  ZoomOut,
  RotateCw,
  Maximize2,
  AlertCircle,
  FileCode,
  FileJson,
  Table,
  Cloud,
  Users,
  Server,
  Shield,
  Database,
  HardDrive,
  Globe,
  Key,
  ChevronDown,
  ChevronRight,
  FileSpreadsheet,
} from 'lucide-react'
import { api } from '../lib/api'
import mammoth from 'mammoth'
import * as XLSX from 'xlsx'

interface EvidencePreviewProps {
  isOpen: boolean
  onClose: () => void
  evidenceId: string
  fileName?: string
  fileType?: string
  fileSize?: number
  note?: string
  controlNumber?: string
  uploadedBy?: string
  timestamp?: number
  source?: string  // e.g., 'azure_integration'
  metadata?: Record<string, any>  // JSON metadata for integration evidence
}

interface TextPreviewResponse {
  success: boolean
  content_type: 'text'
  file_type: string
  file_name: string
  content: string
  truncated: boolean
  original_size: number
  error?: string
}

/**
 * Sanitizes text content to prevent XSS attacks.
 * Escapes HTML entities and removes potentially dangerous content.
 */
function sanitizeTextContent(content: string): string {
  // Escape HTML entities
  const escaped = content
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')

  return escaped
}

/**
 * Gets the appropriate icon for a file type
 */
function getFileTypeIcon(fileType?: string) {
  if (!fileType) return File

  if (fileType.startsWith('image/')) return Image
  if (fileType === 'application/pdf') return FileText
  if (fileType === 'application/json') return FileJson
  if (fileType === 'text/csv') return Table
  if (fileType.startsWith('text/') || fileType === 'application/xml') return FileCode
  // Word documents
  if (fileType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
      fileType === 'application/msword') return FileText
  // Excel files
  if (fileType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
      fileType === 'application/vnd.ms-excel') return FileSpreadsheet

  return File
}

/**
 * Determines the syntax highlighting language based on file type
 */
function getLanguageFromFileType(fileType?: string): string {
  switch (fileType) {
    case 'application/json':
      return 'json'
    case 'text/xml':
    case 'application/xml':
      return 'xml'
    case 'text/markdown':
      return 'markdown'
    case 'text/csv':
      return 'csv'
    default:
      return 'text'
  }
}

/**
 * EvidencePreview component for inline viewing of evidence files.
 * Supports images, PDFs, and text files without requiring download.
 *
 * Features:
 * - Image preview with zoom and rotation controls
 * - PDF preview using browser's native PDF viewer
 * - Text file preview with syntax highlighting for JSON, XML, CSV, Markdown
 * - Fallback download for unsupported file types
 * - File metadata display
 * - Security: XSS protection for text content
 *
 * Issue #163: Evidence Preview and Inline Viewing
 */
export function EvidencePreview({
  isOpen,
  onClose,
  evidenceId,
  fileName,
  fileType,
  fileSize,
  note,
  controlNumber,
  uploadedBy,
  timestamp,
  source,
  metadata,
}: EvidencePreviewProps) {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [zoom, setZoom] = useState(1)
  const [rotation, setRotation] = useState(0)
  const [isFullscreen, setIsFullscreen] = useState(false)

  // Text preview state
  const [textContent, setTextContent] = useState<string | null>(null)
  const [textTruncated, setTextTruncated] = useState(false)
  const [textOriginalSize, setTextOriginalSize] = useState(0)
  const [showLineNumbers, setShowLineNumbers] = useState(true)

  // Word document preview state
  const [wordHtml, setWordHtml] = useState<string | null>(null)

  // Excel preview state
  const [excelData, setExcelData] = useState<{ sheets: Array<{ name: string; data: string[][] }>; activeSheet: number } | null>(null)

  // Blob URL state for authenticated file loading
  const [blobUrl, setBlobUrl] = useState<string | null>(null)

  const downloadUrl = api.getEvidenceDownloadUrl(evidenceId)
  const previewUrl = api.getEvidencePreviewUrl(evidenceId)

  // Check if this is Azure integration evidence (has metadata, no file)
  const isAzureIntegration = source === 'azure_integration' && metadata

  // Determine if file type is previewable
  const isImage = fileType?.startsWith('image/')
  const isPdf = fileType === 'application/pdf'
  const isText = fileType?.startsWith('text/') ||
    fileType === 'application/json' ||
    fileType === 'application/xml'
  const isWord = fileType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
    fileType === 'application/msword'
  const isExcel = fileType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
    fileType === 'application/vnd.ms-excel'
  const isPreviewable = isImage || isPdf || isText || isWord || isExcel || isAzureIntegration

  const FileIcon = getFileTypeIcon(fileType)

  // Fetch text content for text files
  const fetchTextContent = useCallback(async () => {
    if (!isText || !evidenceId) return

    setLoading(true)
    setError(null)

    try {
      const response = await fetch(previewUrl, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('grc_token')}`,
        },
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || `HTTP ${response.status}`)
      }

      const data: TextPreviewResponse = await response.json()

      if (!data.success) {
        throw new Error(data.error || 'Failed to load text content')
      }

      // Sanitize content to prevent XSS
      const sanitized = sanitizeTextContent(data.content)
      setTextContent(sanitized)
      setTextTruncated(data.truncated)
      setTextOriginalSize(data.original_size)
    } catch (err) {
      console.error('Failed to fetch text content:', err)
      setError(err instanceof Error ? err.message : 'Failed to load text content')
    } finally {
      setLoading(false)
    }
  }, [evidenceId, isText, previewUrl])

  // Fetch binary content (images/PDFs) with auth header
  const fetchBinaryContent = useCallback(async () => {
    if (!evidenceId || (!isImage && !isPdf)) return

    setLoading(true)
    setError(null)

    try {
      const response = await fetch(previewUrl, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('grc_token')}`,
        },
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || errorData.message || `HTTP ${response.status}`)
      }

      const blob = await response.blob()
      const url = URL.createObjectURL(blob)
      setBlobUrl(url)
    } catch (err) {
      console.error('Failed to fetch binary content:', err)
      setError(err instanceof Error ? err.message : 'Failed to load file')
    } finally {
      setLoading(false)
    }
  }, [evidenceId, isImage, isPdf, previewUrl])

  // Fetch and parse Word document
  const fetchWordContent = useCallback(async () => {
    if (!evidenceId || !isWord) return

    setLoading(true)
    setError(null)

    try {
      const response = await fetch(downloadUrl, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('grc_token')}`,
        },
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || errorData.message || `HTTP ${response.status}`)
      }

      const arrayBuffer = await response.arrayBuffer()

      // Use mammoth to convert DOCX to HTML
      const result = await mammoth.convertToHtml({ arrayBuffer })
      setWordHtml(result.value)

      if (result.messages.length > 0) {
        console.warn('Mammoth conversion warnings:', result.messages)
      }
    } catch (err) {
      console.error('Failed to parse Word document:', err)
      setError(err instanceof Error ? err.message : 'Failed to load Word document')
    } finally {
      setLoading(false)
    }
  }, [evidenceId, isWord, downloadUrl])

  // Fetch and parse Excel file
  const fetchExcelContent = useCallback(async () => {
    if (!evidenceId || !isExcel) return

    setLoading(true)
    setError(null)

    try {
      const response = await fetch(downloadUrl, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('grc_token')}`,
        },
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || errorData.message || `HTTP ${response.status}`)
      }

      const arrayBuffer = await response.arrayBuffer()

      // Use SheetJS to parse Excel
      const workbook = XLSX.read(arrayBuffer, { type: 'array' })

      const sheets = workbook.SheetNames.map(name => {
        const sheet = workbook.Sheets[name]
        const data = XLSX.utils.sheet_to_json<string[]>(sheet, { header: 1 })
        return { name, data: data as string[][] }
      })

      setExcelData({ sheets, activeSheet: 0 })
    } catch (err) {
      console.error('Failed to parse Excel file:', err)
      setError(err instanceof Error ? err.message : 'Failed to load Excel file')
    } finally {
      setLoading(false)
    }
  }, [evidenceId, isExcel, downloadUrl])

  // Handle authenticated download
  const handleDownload = async () => {
    try {
      const response = await fetch(downloadUrl, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('grc_token')}`,
        },
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || errorData.message || `HTTP ${response.status}`)
      }

      const blob = await response.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = fileName || 'download'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (err) {
      console.error('Download failed:', err)
      setError(err instanceof Error ? err.message : 'Download failed')
    }
  }

  useEffect(() => {
    if (isOpen) {
      setLoading(true)
      setError(null)
      setZoom(1)
      setRotation(0)
      setTextContent(null)
      setTextTruncated(false)
      setBlobUrl(null)
      setWordHtml(null)
      setExcelData(null)

      if (isText) {
        fetchTextContent()
      } else if (isImage || isPdf) {
        fetchBinaryContent()
      } else if (isWord) {
        fetchWordContent()
      } else if (isExcel) {
        fetchExcelContent()
      } else if (isAzureIntegration) {
        // Azure integration metadata is already available, no fetch needed
        setLoading(false)
      }
    }

    // Cleanup blob URL when modal closes
    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl)
      }
    }
  }, [isOpen, evidenceId, isText, isImage, isPdf, isWord, isExcel, isAzureIntegration, fetchTextContent, fetchBinaryContent, fetchWordContent, fetchExcelContent])

  // Handle keyboard shortcuts
  useEffect(() => {
    if (!isOpen) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (isFullscreen) {
          setIsFullscreen(false)
        } else {
          onClose()
        }
      } else if (e.key === '+' || e.key === '=') {
        handleZoomIn()
      } else if (e.key === '-') {
        handleZoomOut()
      } else if (e.key === 'r' || e.key === 'R') {
        handleRotate()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, isFullscreen, onClose])

  const handleZoomIn = () => {
    setZoom((prev) => Math.min(prev + 0.25, 3))
  }

  const handleZoomOut = () => {
    setZoom((prev) => Math.max(prev - 0.25, 0.25))
  }

  const handleRotate = () => {
    setRotation((prev) => (prev + 90) % 360)
  }

  const handleImageLoad = () => {
    setLoading(false)
  }

  const handleImageError = () => {
    setLoading(false)
    setError('Failed to load image. The file may be corrupted or inaccessible.')
  }

  const handlePdfLoad = () => {
    setLoading(false)
  }

  const handlePdfError = () => {
    setLoading(false)
    setError('Failed to load PDF. Try downloading the file instead.')
  }

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return '-'
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  const formatDate = (ts?: number) => {
    if (!ts) return '-'
    return new Date(ts).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  // Render text content with line numbers
  const renderTextContent = () => {
    if (!textContent) return null

    const lines = textContent.split('\n')
    const language = getLanguageFromFileType(fileType)

    return (
      <div className="w-full h-full overflow-auto bg-gray-900 text-gray-100 font-mono text-sm">
        {textTruncated && (
          <div className="sticky top-0 z-10 px-4 py-2 bg-amber-500 text-amber-900 text-sm flex items-center gap-2">
            <AlertCircle className="h-4 w-4" />
            <span>
              Content truncated. Showing first 1MB of {formatFileSize(textOriginalSize)}.{' '}
              <button onClick={handleDownload} className="underline font-medium">
                Download full file
              </button>
            </span>
          </div>
        )}
        <div className="flex">
          {showLineNumbers && (
            <div className="flex-shrink-0 select-none text-gray-500 text-right pr-4 pl-4 py-4 border-r border-gray-700 bg-gray-800">
              {lines.map((_, index) => (
                <div key={index} className="leading-6">
                  {index + 1}
                </div>
              ))}
            </div>
          )}
          <pre className="flex-1 p-4 overflow-x-auto whitespace-pre">
            <code className={`language-${language}`}>
              {lines.map((line, index) => (
                <div key={index} className="leading-6">
                  {/* Using dangerouslySetInnerHTML is safe here because content is already sanitized */}
                  {line || '\u00A0'}
                </div>
              ))}
            </code>
          </pre>
        </div>
      </div>
    )
  }

  // Get icon for Azure resource type
  const getAzureResourceIcon = (type?: string) => {
    switch (type) {
      case 'entra_users':
        return Users
      case 'entra_groups':
        return Users
      case 'virtual_machines':
        return Server
      case 'network_security_groups':
        return Shield
      case 'sql_databases':
        return Database
      case 'storage_accounts':
        return HardDrive
      case 'web_apps':
        return Globe
      case 'key_vaults':
        return Key
      default:
        return Cloud
    }
  }

  // Render Azure integration metadata
  const renderAzureMetadata = () => {
    if (!metadata) return null

    const resourceType = metadata.type || 'unknown'
    const data = metadata.data || []
    const summary = metadata.summary || ''  // summary is a string like "13 Entra ID users"
    const ResourceIcon = getAzureResourceIcon(resourceType)

    // Format resource type for display
    const formatResourceType = (type: string) => {
      return type
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase())
    }

    // Format column header for display (PascalCase to Title Case)
    const formatColumnHeader = (col: string) => {
      // Convert PascalCase to words with spaces
      return col
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (c) => c.toUpperCase())
        .trim()
    }

    // Render a data table for arrays
    const renderDataTable = (items: any[], columns: string[]) => {
      if (!items?.length) return <span className="text-gray-500">No data</span>

      return (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                {columns.map((col) => (
                  <th
                    key={col}
                    className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    {formatColumnHeader(col)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {items.slice(0, 50).map((item, idx) => (
                <tr key={idx} className="hover:bg-gray-50">
                  {columns.map((col) => {
                    const value = item[col]
                    let display: React.ReactNode = '-'

                    if (typeof value === 'boolean') {
                      display = value ? '✓' : '✗'
                    } else if (Array.isArray(value)) {
                      display = value.length > 0 ? value.join(', ') : '-'
                    } else if (value && typeof value === 'object') {
                      // Handle objects like Encryption
                      if (col === 'Encryption') {
                        display = '✓' // If encryption object exists, it's enabled
                      } else {
                        display = JSON.stringify(value)
                      }
                    } else if (value !== null && value !== undefined) {
                      display = String(value)
                    }

                    return (
                      <td key={col} className="px-3 py-2 whitespace-nowrap text-gray-700 max-w-xs truncate">
                        {display}
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          {items.length > 50 && (
            <p className="text-sm text-gray-500 mt-2 px-3">
              Showing 50 of {items.length} items
            </p>
          )}
        </div>
      )
    }

    // Get columns based on resource type (PascalCase matching Azure service output)
    const getColumnsForType = (type: string) => {
      switch (type) {
        case 'entra_users':
          return ['DisplayName', 'UserPrincipalName', 'Email', 'AccountEnabled', 'UserType']
        case 'entra_groups':
          return ['DisplayName', 'Description', 'SecurityEnabled', 'MailEnabled', 'GroupTypes']
        case 'virtual_machines':
          return ['Name', 'Location', 'VmSize', 'OsType', 'ProvisioningState']
        case 'network_security_groups':
          return ['Name', 'Location', 'ResourceGroup']
        case 'sql_databases':
          return ['Name', 'ServerName', 'Location', 'Edition', 'TransparentDataEncryption']
        case 'storage_accounts':
          return ['Name', 'Location', 'Encryption', 'HttpsOnly', 'AllowBlobPublicAccess']
        case 'web_apps':
          return ['Name', 'Location', 'State', 'HttpsOnly', 'DefaultHostName']
        case 'key_vaults':
          return ['Name', 'Location', 'SoftDeleteEnabled', 'PurgeProtectionEnabled']
        default:
          return ['Name', 'Location']
      }
    }

    const columns = getColumnsForType(resourceType)
    const dataItems = Array.isArray(data) ? data : data.items || data.value || [data]

    return (
      <div className="w-full h-full overflow-auto bg-white p-6">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6 pb-4 border-b border-gray-200">
          <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
            <ResourceIcon className="h-6 w-6 text-blue-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              {formatResourceType(resourceType)}
            </h3>
            <p className="text-sm text-gray-500">
              Azure Integration Evidence
            </p>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Cloud className="h-5 w-5 text-blue-500" />
            <span className="text-sm font-medium text-blue-600">Azure</span>
          </div>
        </div>

        {/* Summary Section */}
        {summary && (
          <div className="mb-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <ResourceIcon className="h-8 w-8 text-blue-600" />
                <div>
                  <p className="text-2xl font-bold text-blue-900">{dataItems.length}</p>
                  <p className="text-sm text-blue-700">{summary}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Data Section */}
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-3">
            Resource Data ({dataItems.length} items)
          </h4>
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            {renderDataTable(dataItems, columns)}
          </div>
        </div>

        {/* Raw JSON toggle */}
        <details className="mt-6">
          <summary className="cursor-pointer text-sm text-gray-500 hover:text-gray-700 flex items-center gap-2">
            <ChevronRight className="h-4 w-4 details-open:rotate-90 transition-transform" />
            View Raw JSON
          </summary>
          <pre className="mt-3 p-4 bg-gray-900 text-gray-100 rounded-lg overflow-auto text-xs font-mono max-h-96">
            {JSON.stringify(metadata, null, 2)}
          </pre>
        </details>
      </div>
    )
  }

  if (!isOpen) return null

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center bg-black/80 ${
        isFullscreen ? '' : 'p-4'
      }`}
      onClick={(e) => e.target === e.currentTarget && !isFullscreen && onClose()}
    >
      <div
        className={`bg-white rounded-xl shadow-2xl flex flex-col ${
          isFullscreen
            ? 'w-full h-full rounded-none'
            : 'w-full max-w-5xl max-h-[90vh]'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50 rounded-t-xl">
          <div className="flex items-center gap-3 min-w-0">
            <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${
              isAzureIntegration ? 'bg-blue-100' : 'bg-gray-100'
            }`}>
              {isAzureIntegration ? (
                <Cloud className="h-5 w-5 text-blue-600" />
              ) : (
                <FileIcon className={`h-5 w-5 ${
                  isImage ? 'text-gray-500' :
                  isPdf ? 'text-red-500' :
                  isWord ? 'text-blue-600' :
                  isExcel ? 'text-green-600' :
                  fileType === 'application/json' ? 'text-yellow-500' :
                  isText ? 'text-blue-500' :
                  'text-gray-500'
                }`} />
              )}
            </div>
            <div className="min-w-0">
              <h2 className="font-medium text-gray-900 truncate">
                {fileName || (isAzureIntegration ? 'Azure Integration Evidence' : 'Evidence Preview')}
              </h2>
              <p className="text-xs text-gray-500">
                {isAzureIntegration
                  ? `Azure • ${metadata?.type?.replace(/_/g, ' ') || 'Integration Data'}`
                  : formatFileSize(fileSize)}
                {controlNumber && ` • Control: ${controlNumber}`}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Zoom and rotation controls for images */}
            {isImage && !loading && !error && (
              <>
                <button
                  onClick={handleZoomOut}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
                  title="Zoom out (-)"
                >
                  <ZoomOut className="h-4 w-4" />
                </button>
                <span className="text-sm text-gray-500 min-w-[60px] text-center">
                  {Math.round(zoom * 100)}%
                </span>
                <button
                  onClick={handleZoomIn}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
                  title="Zoom in (+)"
                >
                  <ZoomIn className="h-4 w-4" />
                </button>
                <button
                  onClick={handleRotate}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
                  title="Rotate (R)"
                >
                  <RotateCw className="h-4 w-4" />
                </button>
                <div className="w-px h-6 bg-gray-200 mx-1" />
              </>
            )}

            {/* Line numbers toggle for text files */}
            {isText && !loading && !error && textContent && (
              <>
                <button
                  onClick={() => setShowLineNumbers(!showLineNumbers)}
                  className={`p-2 rounded-lg ${
                    showLineNumbers
                      ? 'text-primary-600 bg-primary-50'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                  }`}
                  title={showLineNumbers ? 'Hide line numbers' : 'Show line numbers'}
                >
                  <span className="text-xs font-mono">123</span>
                </button>
                <div className="w-px h-6 bg-gray-200 mx-1" />
              </>
            )}

            <button
              onClick={() => setIsFullscreen(!isFullscreen)}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
              title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
            >
              <Maximize2 className="h-4 w-4" />
            </button>

            {/* Hide download buttons for Azure integration (no file to download) */}
            {!isAzureIntegration && (
              <>
                <button
                  onClick={handleDownload}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
                  title="Download"
                >
                  <Download className="h-4 w-4" />
                </button>

                {blobUrl && (
                  <a
                    href={blobUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
                    title="Open in new tab"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </a>
                )}
              </>
            )}

            <button
              onClick={onClose}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
              title="Close (Esc)"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className={`flex-1 bg-gray-100 relative ${(isAzureIntegration || isWord || isExcel) ? 'overflow-auto' : 'overflow-hidden'}`}>
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
              <div className="text-center">
                <Loader2 className="h-8 w-8 text-gray-400 animate-spin mx-auto mb-2" />
                <p className="text-sm text-gray-500">Loading preview...</p>
              </div>
            </div>
          )}

          {error && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
              <div className="text-center p-6">
                <AlertCircle className="h-16 w-16 text-red-300 mx-auto mb-4" />
                <p className="text-gray-600 mb-4">{error}</p>
                <button
                  onClick={handleDownload}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                  <Download className="h-4 w-4" />
                  Download File
                </button>
              </div>
            </div>
          )}

          {isImage && blobUrl && (
            <div
              className="w-full h-full overflow-auto flex items-center justify-center p-4"
              style={{ minHeight: '400px' }}
            >
              <img
                src={blobUrl}
                alt={fileName || 'Evidence'}
                onLoad={handleImageLoad}
                onError={handleImageError}
                className="max-w-full max-h-full object-contain transition-transform duration-200"
                style={{
                  transform: `scale(${zoom}) rotate(${rotation}deg)`,
                }}
              />
            </div>
          )}

          {isPdf && blobUrl && (
            <iframe
              src={`${blobUrl}#toolbar=1&navpanes=0`}
              className="w-full h-full border-0"
              style={{ minHeight: '500px' }}
              onLoad={handlePdfLoad}
              onError={handlePdfError}
              title={fileName || 'PDF Preview'}
            />
          )}

          {isText && !loading && !error && textContent && (
            <div className="w-full h-full" style={{ minHeight: '400px' }}>
              {renderTextContent()}
            </div>
          )}

          {/* Word Document Preview */}
          {isWord && !loading && !error && wordHtml && (
            <div className="w-full h-full overflow-auto bg-white" style={{ minHeight: '400px' }}>
              <div className="max-w-4xl mx-auto p-8">
                <div
                  className="prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: wordHtml }}
                />
              </div>
            </div>
          )}

          {/* Excel Preview */}
          {isExcel && !loading && !error && excelData && (
            <div className="w-full h-full overflow-auto bg-white" style={{ minHeight: '400px' }}>
              {/* Sheet tabs */}
              {excelData.sheets.length > 1 && (
                <div className="sticky top-0 z-10 bg-gray-100 border-b flex gap-1 p-2 overflow-x-auto">
                  {excelData.sheets.map((sheet, idx) => (
                    <button
                      key={sheet.name}
                      onClick={() => setExcelData({ ...excelData, activeSheet: idx })}
                      className={`px-3 py-1.5 text-sm font-medium rounded whitespace-nowrap ${
                        excelData.activeSheet === idx
                          ? 'bg-white text-primary-600 shadow'
                          : 'text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {sheet.name}
                    </button>
                  ))}
                </div>
              )}
              {/* Sheet data */}
              <div className="overflow-auto">
                <table className="min-w-full border-collapse">
                  <tbody>
                    {excelData.sheets[excelData.activeSheet]?.data.slice(0, 500).map((row, rowIdx) => (
                      <tr key={rowIdx} className={rowIdx === 0 ? 'bg-gray-100 font-medium' : rowIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        {/* Row number */}
                        <td className="px-2 py-1 text-xs text-gray-400 border border-gray-200 bg-gray-50 text-center min-w-[40px]">
                          {rowIdx + 1}
                        </td>
                        {row.map((cell, cellIdx) => (
                          <td
                            key={cellIdx}
                            className="px-3 py-1.5 text-sm border border-gray-200 whitespace-nowrap max-w-xs truncate"
                            title={String(cell ?? '')}
                          >
                            {cell ?? ''}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {excelData.sheets[excelData.activeSheet]?.data.length > 500 && (
                  <div className="p-4 text-center text-sm text-gray-500 bg-amber-50 border-t">
                    Showing first 500 rows of {excelData.sheets[excelData.activeSheet].data.length} total rows.
                    <button onClick={handleDownload} className="text-primary-600 hover:underline ml-2">
                      Download full file
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {isAzureIntegration && (
            <div className="w-full h-full overflow-auto" style={{ minHeight: '400px' }}>
              {renderAzureMetadata()}
            </div>
          )}

          {!isPreviewable && !loading && (
            <div className="flex items-center justify-center h-full p-6">
              <div className="text-center">
                <File className="h-20 w-20 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Preview Not Available
                </h3>
                <p className="text-gray-500 mb-6">
                  This file type ({fileType || 'unknown'}) cannot be previewed in the browser.
                </p>
                <button
                  onClick={handleDownload}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium"
                >
                  <Download className="h-5 w-5" />
                  Download to View
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer with metadata */}
        <div className="px-4 py-3 border-t border-gray-200 bg-gray-50 rounded-b-xl">
          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
            {note && (
              <div className="flex-1 min-w-[200px]">
                <span className="font-medium text-gray-700">Note:</span>{' '}
                <span className="text-gray-600">{note}</span>
              </div>
            )}
            <div className="flex flex-wrap items-center gap-4 ml-auto">
              {uploadedBy && (
                <span>
                  <span className="font-medium text-gray-700">By:</span> {uploadedBy}
                </span>
              )}
              {timestamp && (
                <span>
                  <span className="font-medium text-gray-700">Uploaded:</span>{' '}
                  {formatDate(timestamp)}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * Preview button for evidence items
 */
export function EvidencePreviewButton({
  onClick,
  disabled = false,
  fileType,
}: {
  onClick: () => void
  disabled?: boolean
  fileType?: string
}) {
  const isPreviewable =
    fileType?.startsWith('image/') ||
    fileType === 'application/pdf' ||
    fileType?.startsWith('text/') ||
    fileType === 'application/json' ||
    fileType === 'application/xml' ||
    // Word documents
    fileType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
    fileType === 'application/msword' ||
    // Excel files
    fileType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
    fileType === 'application/vnd.ms-excel'

  if (!isPreviewable) {
    return null
  }

  const FileIcon = getFileTypeIcon(fileType)

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="p-2 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded transition-colors"
      title="Preview file"
    >
      <FileIcon className="h-4 w-4" />
    </button>
  )
}

export default EvidencePreview
